import { DEFAULT_MOS_ID } from './mos-interfaces';
import { getChild, getValueOrNullFromTag } from './mos-util';
import { isValidGUID } from '../util/general';
import { MosBuilder, MosMessageHistory } from './mos';
import type { MosObjOptions , MosEventHandler, MosItemReplaceData, MosObjCompatibleItem, MosOpenItemInfo } from './mos-interfaces';
import type { Logger } from '../types/logger';
import { exceptionToString } from '../util/exception';

export default class ClientMosHelper {

  public readonly origin: string;
  public readonly useNcsItem;

  private _ncsId: string | null = null;
  private _ncsUserId: string | null = null;
  private _mosId: string;

  private parser: DOMParser;
  private postMessage: (data: string, origin: string) => void;
  private mosHistory = new MosMessageHistory();
  private eventHandler: MosEventHandler;

  private awaitingNcsAppInfo = false;
  private imageObjectPathPrefix: string | null = null;
  private readonly includeObjProxyPath: boolean;

  public mosOpenItemInfo: MosOpenItemInfo;

  constructor(
    private logger: Logger,
    origin: string,
    postMessage: (data: string, origin: string) => void,
    domParser: DOMParser,
    eventHandler: MosEventHandler,
    useNcsItem: boolean = false,
    iopp: string | null = null,
    customMosId = DEFAULT_MOS_ID,
    includeObjProxyPath = false,
    mosOpenItemInfo: MosOpenItemInfo = { currentMosOpenItemId: null, mosItemReplaceRequestId: null }
  ) {
    this.parser = domParser;
    this.origin = origin;
    this.postMessage = postMessage;
    this.eventHandler = eventHandler;
    this._mosId = customMosId;
    this.useNcsItem = useNcsItem;
    this.imageObjectPathPrefix = iopp;
    this.includeObjProxyPath = includeObjProxyPath;
    this.mosOpenItemInfo = mosOpenItemInfo;
  }

  get ncsId() {
    return this._ncsId;
  }

  get ncsUserId() {
    return this._ncsUserId;
  }

  get mosId() {
    return this._mosId;
  }

  mosMessageFromHost(event: Pick<MessageEvent, 'origin' | 'data' | 'source'>) {
    if (event.origin !== this.origin) {
      this.logger.warn('Origin does not match, ignoring message.');
    } else {

      if (typeof event.data !== 'string' || event.data.startsWith('webpack')) {
        return;
      }

      const msg = this.mosHistory.receivedFromHost(event.data);

      let isValidXml = true;
      let isValidMosMessage = false;
      let doc: Document | null = null;
      let mosRoot: Node | null = null;
      try {
        doc = this.parseToXmlDoc(event.data.trim());
        mosRoot = doc.firstChild;
        isValidMosMessage = mosRoot?.nodeName === 'mos';
      } catch (err) {
        this.logger.warn(`Failed parsing xml ${exceptionToString(err)}`);
        isValidXml = false;
      }

      if (!isValidXml || !isValidMosMessage) {
        this.sendMessage(this.newMosBuilder().ncsAck('ERROR', !isValidXml ? 'Invalid XML detected' : 'Did not recognize message as MOS (missing <mos> node)').serialize(), msg.key);
      } else if (mosRoot) {
        // Find out what we received and whether we should respond anything.
        this.handleMessage(mosRoot, msg.key);
      }
    }
  }

  parseToXmlDoc(text: string) {
    const doc = this.parser.parseFromString(text, 'application/xml');
    if (doc.documentElement.tagName === 'parsererror') {
      throw new Error(`Failed parsing to xml: ${doc.documentElement.textContent}`);
    }
    return doc;
  }

  notifyHost() {
    this.awaitingNcsAppInfo = true;
    this.sendMessage(this.newMosBuilder().ncsReqAppInfo().serialize());
  }

  getMosHistory() {
    return this.mosHistory;
  }

  createMosXmlFor(item: MosObjCompatibleItem) {
    const mosOptions: MosObjOptions = { includeObjProxyPath: this.includeObjProxyPath };
    if (this.imageObjectPathPrefix !== null) {
      mosOptions.imageObjectPathPrefix = this.imageObjectPathPrefix;
    }
    if (this.useNcsItem) {
      return this.newMosBuilder().ncsItem(item, mosOptions).serialize();
    }
    return this.newMosBuilder().mosObj(item, mosOptions).serialize();
  }

  createMosItemReplaceXmlFor(item: MosObjCompatibleItem, mosItemReplaceData: MosItemReplaceData) {
    const mosOptions: MosObjOptions = { includeObjProxyPath: this.includeObjProxyPath };
    if (this.imageObjectPathPrefix !== null) {
      mosOptions.imageObjectPathPrefix = this.imageObjectPathPrefix;
    }
    return this.newMosBuilder().mosItemReplace(item, mosOptions, mosItemReplaceData).serialize();
  }

  private handleMessage(mosRoot: Node, responseKey?: string) {

    const ncsAppInfo = getChild(mosRoot, 'ncsAppInfo');
    if (ncsAppInfo !== null) {
      this._ncsUserId = getValueOrNullFromTag(ncsAppInfo, 'ncsInformation', 'userID');
      this._ncsId = getValueOrNullFromTag(mosRoot, 'ncsID');

      // Check if there's an item embedded to open. For now we only
      // care about the item id, if embedded and attempt to open that.
      const itemId = getChild(ncsAppInfo, 'item', 'objID');
      if (itemId?.textContent && isValidGUID(itemId?.textContent)) {
        let mosItemReplaceData: MosItemReplaceData | null = null;
        const storyId = getValueOrNullFromTag(ncsAppInfo, 'storyID');
        const itemIdInStory = getValueOrNullFromTag(ncsAppInfo, 'item', 'itemID');
        if (storyId && itemIdInStory) {
          mosItemReplaceData = { storyId, itemIdInStory };
        }
        this.eventHandler.openItemId(itemId.textContent, mosItemReplaceData);
      }
      // MOS Protocol states we only ACK if the message was unsollicited.
      if (!this.awaitingNcsAppInfo) {
        this.sendMessage(this.newMosBuilder().ncsAck('ACK').serialize(), responseKey);
      } else {
        this.awaitingNcsAppInfo = false;
      }
      return;
    }

    const ncsItem = getChild(mosRoot, 'ncsItem');
    if (ncsItem !== null) {
      const itemId = getChild(ncsItem, 'item', 'objID');
      if (itemId?.textContent && isValidGUID(itemId?.textContent)) {
        this.eventHandler.openItemId(itemId.textContent);
      }
      return;
    }

    const ncsItemRequest = getChild(mosRoot, 'ncsItemRequest');
    if (ncsItemRequest !== null) {
      const item = this.eventHandler.requestSelectedItem();
      if (item === null) {
        this.sendMessage(this.newMosBuilder().ncsAck('ERROR').serialize(), responseKey);
      } else {
        this.sendMessage(this.newMosBuilder().ncsItemWithMosObj(item).serialize(), responseKey);
      }
      return;
    }

    const ncsAck = getChild(mosRoot, 'ncsAck');
    if (ncsAck !== null) {
      const status = getChild(ncsAck, 'status');
      // for now we only check if it's an ACK if we have requested a mosItemReplace
      if (status?.textContent === 'ACK' && this.mosOpenItemInfo.mosItemReplaceRequestId) {
        this.mosOpenItemInfo.currentMosOpenItemId = this.mosOpenItemInfo.mosItemReplaceRequestId;
        this.mosOpenItemInfo.mosItemReplaceRequestId = null;
      }
      // No need to respond to these.
      this.logger.info('Received ncsAck');
      return;
    }

    this.logger.warn('Dont know how to handle this message yet');
    this.sendMessage(this.newMosBuilder().ncsAck('ERROR').serialize(), responseKey);
  }

  sendMessage(message: string, responseKey?: string) {
    this.mosHistory.sentToHost(message, responseKey);
    this.postMessage(message, this.origin);
  }

  private newMosBuilder() {
    if (this._ncsId) {
      return new MosBuilder({
        ncsID: this._ncsId,
        mosID: this._mosId,
        ...(this._ncsUserId ? {userID: this._ncsUserId} : {})
      });
    }
    return new MosBuilder({ mosID: this._mosId });
  }
}
